import { uuid } from "vue-uuid";
import { readCookie } from "@/util/cookieMonitor";

export interface S3UploadResult {
  Location: string;
  Bucket: string;
  key: string;
}

export const getAlbumNameKey = (): string => {
  const album =
    (
      {
        production: "",
        local: "staging",
      } as Record<string, string>
    )[import.meta.env.VITE_APP_ENV] ?? import.meta.env.VITE_APP_ENV;
  return album === "" ? "" : `${encodeURIComponent(album)}/`;
};

export const s3Upload = async (
  file: File,
  newName: string,
  albumNameKey = getAlbumNameKey(),
): Promise<S3UploadResult> => {
  const fileKey = albumNameKey + newName;

  const formData = new FormData();
  formData.append("file", file);
  formData.append("fileKey", fileKey);

  const response = await fetch("/api/s3-upload", {
    method: "POST",
    headers: {
      "X-XSRF-Token": readCookie("XSRF-TOKEN") ?? "",
    },
    body: formData,
  });
  const s3 = await response.json();
  if (!s3.result.Location) {
    throw new Error("Upload connection failed");
  }
  return {
    Location: s3.result.Location,
    Bucket: s3.result.Bucket,
    key: fileKey,
  };
};

export const createUUIDName = (name: string): string => uuid.v4() + extractFileExtension(name);

export const fileName = (name: string): string => {
  const nameParts = name.split(".");
  const extension = nameParts.pop() ?? "";

  const maxFileNameLength = 79 - extension.length;
  const baseName = nameParts.join(".").substring(0, maxFileNameLength);

  return [baseName, extension].join(".");
};

export const validateFormName = (formName: string): void => {
  if (!formName) {
    throw new Error("Form Name is required.");
  }
};

export const extractFileExtension = (originalFile: string): string => {
  const lastIndex = originalFile.lastIndexOf(".");
  return originalFile.substring(lastIndex);
};

export const generateUniqueString = (): string => uuid.v4().slice(0, 5);

export const sanitizeNameComponent = (value: string | null | undefined): string => {
  if (!value) return "";
  return value.replace(/[\s-]/g, "_");
};

export const limitFileNameLength = (name: string): string => (name.length > 80 ? name.slice(0, 80) : name);

// Desired format: Last Name_First Name_Form Name_UniqueString
export const createFileName = (
  originalFile: string,
  formName: string,
  firstName: string | null | undefined = undefined,
  lastName: string | null | undefined = undefined,
): string => {
  validateFormName(formName);

  const fileExtension = extractFileExtension(originalFile);
  const uniqueString = generateUniqueString();

  const nameComponents = [
    sanitizeNameComponent(lastName),
    sanitizeNameComponent(firstName),
    sanitizeNameComponent(formName),
  ].filter((component) => component !== "");

  const fileNameWithRandomString = limitFileNameLength(`${nameComponents.join("_")}_${uniqueString}`);
  return `${fileNameWithRandomString}${fileExtension}`;
};
